/**
 * Keeps the product info column in view as one block while the page scrolls (two-column layout only).
 * A column that fits on screen sticks below the header. A taller one moves with the page until its bottom is on
 * screen and stays there; scrolling back up, it moves with the page until its top is on screen and stays there.
 * It works by moving the sticky `top` between those two positions, so the column never covers other content.
 */
if (!customElements.get('sticky-column')) {
  const DESKTOP_QUERY = '(min-width: 768px)';
  const GAP_PX = 16;
  const TOP_PROPERTY = '--sticky-column-top';

  class StickyColumn extends HTMLElement {
    constructor() {
      super();
      this.desktop = window.matchMedia(DESKTOP_QUERY);
      this.top = null;
      this.minTop = 0;
      this.maxTop = 0;
      this.lastScrollY = window.scrollY;
      this.ticking = false;
      this.headerHeight = null;
      this.update = this.update.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onRootStyle = this.onRootStyle.bind(this);
    }

    connectedCallback() {
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this);
      // main.js sets --header-height on DOMContentLoaded and resize, after this deferred script has run.
      this.rootObserver = new MutationObserver(this.onRootStyle);
      this.rootObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
      this.desktop.addEventListener('change', this.update);
      window.addEventListener('on:debounced-resize', this.update);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver.disconnect();
      this.rootObserver.disconnect();
      this.desktop.removeEventListener('change', this.update);
      window.removeEventListener('on:debounced-resize', this.update);
      window.removeEventListener('scroll', this.onScroll);
    }

    readHeaderHeight() {
      const root = document.documentElement;
      return root.style.getPropertyValue('--header-height') || getComputedStyle(root).getPropertyValue('--header-height');
    }

    // The root style also changes on every scroll (--mega-menu-top), so only react to a new header height.
    onRootStyle() {
      if (this.readHeaderHeight() !== this.headerHeight) this.update();
    }

    update() {
      this.headerHeight = this.readHeaderHeight();
      if (!this.desktop.matches) {
        this.top = null;
        this.style.removeProperty(TOP_PROPERTY);
        return;
      }

      if (this.top === null) this.lastScrollY = window.scrollY;
      const wasAtTop = this.top === null || this.top >= this.maxTop;
      this.maxTop = (parseFloat(this.headerHeight) || 0) + GAP_PX;
      this.minTop = Math.min(this.maxTop, window.innerHeight - this.offsetHeight - GAP_PX);
      this.setTop(wasAtTop ? this.maxTop : this.top);
    }

    onScroll() {
      if (this.ticking || this.top === null) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.ticking = false;
        const delta = window.scrollY - this.lastScrollY;
        this.lastScrollY = window.scrollY;
        this.setTop(this.top - delta);
      });
    }

    setTop(value) {
      const top = Math.min(this.maxTop, Math.max(this.minTop, value));
      if (top === this.top) return;
      this.top = top;
      this.style.setProperty(TOP_PROPERTY, `${top}px`);
    }
  }

  customElements.define('sticky-column', StickyColumn);
}
